import { useMemo } from 'react'

import {
  type LegacyFilter,
  type RawInlineFilter,
  useListFilters,
  useInlineFilters,
} from '@tribeplatform/react-components/Filters'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'

import { convertToMemberListFilter } from '../utils/convertToMemberListFilter.js'
import { staticFiltersFactory } from '../utils/staticFilters.js'
import { useCustomFields } from './useCustomFields.js'

interface useMemberFiltersProps {
  filterBy: LegacyFilter[]
  rawInlineFilters: RawInlineFilter[]
}
export const useMemberFilters = ({
  filterBy,
  rawInlineFilters,
}: useMemberFiltersProps) => {
  const { data: network } = useNetwork()
  const { memberFields: customFields } = useCustomFields({
    excludePrivateFields: true,
  })
  const staticFilters = useStaticIntl(staticFiltersFactory)
  const ListFilters = useMemo(
    () => convertToMemberListFilter(filterBy, network?.roles),
    [filterBy, network?.roles],
  )
  const { filters, allFilters, prepareFilters } = useListFilters({
    entityListFilters: ListFilters,
    customFields,
    staticFilters,
  })
  const {
    processedInlineFilters,
    setProcessedInlineFilter,
    setProcessedInlineFilters,
    resetProcessedInlineFilter,
  } = useInlineFilters({
    entityListFilters: ListFilters,
    filters,
    allFilters,
    rawInlineFilters,
  })
  const combinedFilters = useMemo(
    () => prepareFilters([...filters, ...processedInlineFilters]),
    [filters, prepareFilters, processedInlineFilters],
  )
  const hasInlineFilters = rawInlineFilters.some(({ enabled }) => !!enabled)

  return {
    processedInlineFilters,
    setProcessedInlineFilter,
    setProcessedInlineFilters,
    resetProcessedInlineFilter,
    combinedFilters,
    hasInlineFilters,
  }
}
